import type { OrderAppDeliveryMode } from '@/graphql/orderApp/api';
import type { CheckoutSsrValue } from '@/providers/CheckoutSsrProvider';

/**
 * The parts of the checkout snapshot this merge touches. Kept structural so
 * the function can be unit-tested without building a whole CheckoutState.
 */
export type MergeableCheckoutSnapshot = {
  /**
   * Deliberately `unknown`, and not an oversight.
   *
   * CheckoutState<true> declares its checkout as
   * DeepNonNullable<CheckoutFragment>, which the store does not actually uphold
   * at runtime - a first-visit guest legitimately has a null shippingAddress.
   * Constraining to that type would assert a fiction and reject the real
   * payload; constraining to CheckoutFragment would not match the Require=true
   * variant. So the parity is enforced at the source instead:
   * CheckoutSsrValue['checkout'] is declared as
   * NonNullable<CheckoutQuery['checkout']>, the exact type the client's own
   * checkoutRestore produces from the same CheckoutDocument.
   */
  checkout: unknown;
  deliveryMethods: readonly OrderAppDeliveryMode[];
};

/**
 * Overlay the server-rendered guest payload onto the client store snapshot
 * (BEC-25061).
 *
 * Per-field, not all-or-nothing: the client fills the store in stages, so
 * handing over wholesale the moment it has a checkout drops values it has not
 * fetched yet.
 *
 * @param isSsrCheckoutStandingIn whether the server payload should still stand
 * in for the store's checkout. Two conditions, both required:
 *
 * 1. the store has never owned a checkout on this mount - it is NOT enough
 *    that a payload exists, since client-side navigation re-runs
 *    getServerSideProps and produces one even with a warm store; and
 * 2. the client is still trying - once its restore has settled without a
 *    checkout, the payload must stop standing in so the caller's empty-basket
 *    redirect can fire. Otherwise a failed restore leaves a form on screen
 *    that can never be submitted and never redirects.
 */
export const mergeSsrSnapshot = <T extends MergeableCheckoutSnapshot>(
  storeSnapshot: T,
  ssr: CheckoutSsrValue | null,
  isSsrCheckoutStandingIn: boolean,
): T => {
  if (!ssr) return storeSnapshot;

  /**
   * `storeSnapshot.checkout === null` is ambiguous on its own: before the
   * handover it means "not restored yet", but afterwards it means
   * "deliberately cleared" - checkoutActions.clear() on sign-out, region
   * change, an already-paid order, or a Saleor error - or "restored and came
   * back with nothing". isSsrCheckoutStandingIn is what separates those from
   * the one case the payload is for; substituting in any of the others would
   * resurrect a dead checkout and suppress the empty-basket redirect.
   */
  const needsSsrCheckout = isSsrCheckoutStandingIn && !storeSnapshot.checkout;

  /**
   * Deliberately not gated on isSsrCheckoutStandingIn. useAssignCheckoutDefaults
   * calls deliveryMethodsUpdate from an effect keyed on [checkout?.id, user],
   * and deliveryMethodsUpdate reads checkoutState directly and early-returns
   * while the store is empty. Because the merged id is the server's from the
   * very first render, restoring the same checkout does not change that dep,
   * so the effect never re-runs and the store never gets its own methods -
   * the delivery options would vanish after hydration.
   */
  const needsSsrDeliveryMethods = !storeSnapshot.deliveryMethods?.length;

  /**
   * Steady state: return the tracked snapshot by identity. Spreading it would
   * touch every key and defeat valtio's access tracking, re-rendering every
   * consumer on any checkoutState change.
   */
  if (!needsSsrCheckout && !needsSsrDeliveryMethods) return storeSnapshot;

  return {
    ...storeSnapshot,
    checkout: needsSsrCheckout ? ssr.checkout : storeSnapshot.checkout,
    deliveryMethods: needsSsrDeliveryMethods
      ? ssr.deliveryMethods
      : storeSnapshot.deliveryMethods,
  };
};
